import { List, fromJS, Record, Map } from "immutable";
import { ActionType } from "typesafe-actions";

import { ImmutableMap } from "src/helpers/ImmutableHelpers";
import { ServicesActionTypes } from "src/action-types/Services";
import * as actions from "src/actions/Services";

import { IServiceUser } from "src/reducers/UpsertServiceForm";
import {
  setServiceDetails as transformServiceDetails,
} from "src/transformators/Services";

export type ServicesActions = ActionType<typeof actions>;

/* ──────────────── typy pomocnicze ──────────────── */
interface ICompany {
  city: string | null;
  house_number: string | null;
  name: string | null;
  post_code: string | null;
  street: string | null;
  nip: string | null;
}

export interface IServiceFile {
  id: number;
  created_at: string;
  original_name: string;
  type: any;
}

export interface IClient {
  id: number;
  first_name: string | null;
  last_name: string | null;
  city: string | null;
  email: string | null;
  house_number: string | null;
  phone: string | null;
  post_code: string | null;
  street: string | null;
}

export interface IServiceCall {
  amount: number | null;
  duration: number | null;
  caller_id: number | null;
  created_at: string | null;
  type: number;
}

export interface IServiceDetails {
  service_id: number | null;
  people: IServiceUser[];
  files: IServiceFile[];
  description: string | null;
  company: ICompany;
  client: IClient;
  steps: any[];
  calls: IServiceCall[];
  additional_information: string | null;
  consequences: string | null;
  messages: any[];
  templates: any[];
  documents: any[];
  before_state: string | null;
  sid: number | null;
}

export interface IServiceDetailsImmutable {
  service_id: number | null;
  people: List<ImmutableMap<IServiceUser>>;
  description: string | null;
  company: ImmutableMap<ICompany>;
  client: ImmutableMap<IClient>;
  steps: List<ImmutableMap<any>>;
  calls: List<ImmutableMap<IServiceCall>>;
  additional_information: string | null;
  consequences: string | null;
  messages: List<ImmutableMap<any>>;
  templates: List<ImmutableMap<any>>;
  documents: List<ImmutableMap<any>>;
  before_state: string | null;
  sid: number | null;
}

/* ───────────── stan modułu Services ───────────── */
export interface IServicesState {
  activeServiceId: number | null;
  changeActiveTab: number | null;
  services: List<ImmutableMap<any>>;
  details: ImmutableMap<IServiceDetailsImmutable>;
  serviceUpdateSuccessful: boolean;
  serviceListLoading: boolean;
  attachment: any | null;
  numPages: number | null;
  upload_finish: boolean | null;

  /* liczniki */
  newRecordsCount: number;        // Usługi
  analyzesNewCount: number;
  withoutAnalyzeCount: number;    // dla prawnika – „bez analizy”

  newRecordsLoading: boolean;
  newRecordsError: any;

  skipFirstRecordReset: boolean;
  lastTab: number;
}

export type ServicesState = Record<IServicesState>;

const ServicesStateFactory = Record<IServicesState>({
  activeServiceId: null,
  changeActiveTab: null,
  services: List(),
  details: Map(),
  serviceUpdateSuccessful: false,
  serviceListLoading: false,
  attachment: null,
  numPages: 0,
  upload_finish: false,

  newRecordsCount: 0,     // Usługi
  analyzesNewCount: 0,    // Analizy
  withoutAnalyzeCount: 0,

  newRecordsLoading: false,
  newRecordsError: null,

  skipFirstRecordReset: false,
  lastTab: 0,
});

const initialState: ServicesState = new ServicesStateFactory();

/* ───────────────────────── reducer ───────────────────────── */
export default function services(
  state: ServicesState = initialState,
  action: ServicesActions
): ServicesState {
  switch (action.type) {
    /* ─────────── liczniki nowych rekordów ─────────── */
    case ServicesActionTypes.FETCH_NEW_RECORDS_COUNT_REQUEST:
      return state
        .set("newRecordsLoading", true)
        .set("newRecordsError", null);

    // Usługi (stary licznik)
    case ServicesActionTypes.FETCH_NEW_RECORDS_COUNT_SUCCESS:
      return state
        .set("newRecordsLoading", false)
        .set("newRecordsCount", action.payload);

    case ServicesActionTypes.FETCH_NEW_RECORDS_COUNT_FAILURE:
      return state
        .set("newRecordsLoading", false)
        .set("newRecordsError", action.payload);

    // Analizy (nowy, osobny licznik)
    case ServicesActionTypes.FETCH_ANALYZE_NEW_COUNT_SUCCESS:
      return state.set("analyzesNewCount", action.payload);

    // Dodatkowy licznik dla prawnika
    case ServicesActionTypes.FETCH_WITHOUT_ANALYZE_COUNT_SUCCESS:
      return state.set("withoutAnalyzeCount", action.payload);

    case ServicesActionTypes.SET_SERVICE_LIST_LOADING:
      return state.set("serviceListLoading", action.payload);

    case ServicesActionTypes.SET_SERVICE_UPDATE_SUCCESSFUL:
      return state.set("serviceUpdateSuccessful", action.payload);

    case ServicesActionTypes.VIEW_ATTACHMENTS_STATE:
      return state.set("attachment", action.payload);

    case ServicesActionTypes.CLOSE_VIEW_ATTACHMENTS_STATE:
      return state.set("attachment", null);

    case ServicesActionTypes.SET_NUM_PAGES:
      return state.set("numPages", action.payload);

    case ServicesActionTypes.SET_UPLOADED_FINISH:
      return state.set("upload_finish", action.payload);

    case ServicesActionTypes.ADD_SERVICE_SUCCESS: {
      const newService = fromJS(action.payload);
      return state.update("services", (list: List<any>) => list.unshift(newService));
    }

    case ServicesActionTypes.SERVICE_ID_SET:
      if (action.payload == null) {
        return state.set("activeServiceId", action.payload).set("details", Map());
      }
      return state.set("activeServiceId", action.payload);

    case ServicesActionTypes.CHANGE_ACTIVE_TAB:
      return state.set("changeActiveTab", action.payload);

    case ServicesActionTypes.SERVICES_LIST_SET:
      return state.set("services", fromJS(action.payload.service_list));

    case ServicesActionTypes.GET_SERVICE_WITH_DOCS_REQUEST:
      return state.set("details", Map());

    case ServicesActionTypes.GET_SERVICE_WITH_DOCS_SUCCESS:
      return state.set("details", fromJS(transformServiceDetails(action.payload)));

    case ServicesActionTypes.GET_SERVICE_WITH_DOCS_FAILURE:
      return state;

    case ServicesActionTypes.SERVICE_DETAILS_SET:
      return state.set("details", fromJS(transformServiceDetails(action.payload)));

    case ServicesActionTypes.SERVICE_STEPS_SET:
      return state.setIn(["details", "steps"], action.payload);

    case ServicesActionTypes.EDIT_SERVICE: {
      const newData = action.payload;
      const idx = state
        .get("services")
        .findIndex((el: ImmutableMap<any>) => el.get("id") === state.get("activeServiceId"));
      let service = state.getIn(["services", idx]) as ImmutableMap<any>;
      Object.keys(newData).forEach((key) => {
        service = service.set(key, newData[key]);
      });
      return state.setIn(["services", idx], service);
    }

    case ServicesActionTypes.SKIP_FIRST_RECORD_RESET:
      return state.set("skipFirstRecordReset", true);

    case ServicesActionTypes.CLEAR_FIRST_RECORD_RESET:
      return state.set("skipFirstRecordReset", false);

    case ServicesActionTypes.SET_LAST_TAB:
      return state.set("lastTab", action.payload);

    default:
      return state;
  }
}
